import React, { useState, useMemo } from 'react';
import { Modal, Typography, Checkbox, Button } from '@deeptrue-frontend/ui';
import styled from 'styled-components/macro';
import {
  useFormatMessage,
  FormattedHTMLMessage,
} from 'components/IntlProvider';
import VoucherInfo from '../VoucherInfo';

const Title = styled(Typography)`
  font-weight: 500;
  font-size: 1.25rem;
  color: #253858;
`;

const Content = styled(Typography)`
  white-space: break-spaces;
  padding: 0 1.5rem;
  margin-top: 5.5rem;
  display: inline-block;
`;

const Terms = styled.p`
  font-size: 0.875rem;
  font-width: 500;
  line-height: 1.25rem;
  margin: 0;
`;

const TermsWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  align-items: center;
  margin: 1.5rem 2.25rem;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  border-bottom: 1px solid #e3e5e8;
  padding: 1.5rem;
`;

const SmallButton = styled(Button)`
  padding: 0.5rem 1rem;
  line-height: 1;
`;

const ButtonWrapper = styled.div`
  border-top: 1px solid #e3e5e8;
  padding: 1.5rem 1rem;
  display: flex;
  justify-content: flex-end;
`;

type DataCollectionLinkModalProps = {
  isVisible: boolean;
  onBackdropClick: any;
  onCloseClick: any;
  onAcceptTerms: any;
  credits: number;
  isLoading?: boolean;
  copyLinkToClipboard?: boolean;
  voucher: any;
};

const DataCollectionLinkModal = (props: DataCollectionLinkModalProps) => {
  const t = useFormatMessage();
  const [isAccepted, setIsAccepted] = useState(false);
  const {
    isVisible,
    onBackdropClick,
    onCloseClick,
    onAcceptTerms,
    credits,
    isLoading,
    voucher,
  } = props;

  const isVoucher = !!(voucher && voucher.voucher);

  const projectPrice = isVoucher ? credits + voucher.claimed_value : credits;
  const projectPriceBeforeDiscount = credits;

  const modalTermsContent = useMemo(
    () => (
      <FormattedHTMLMessage
        id="project.collectData.linkModal.termsAndConditions"
        defaultMessage="I agree to deeptrue {link} and sign a contract."
        values={{
          link: (
            <a
              href={t(
                'project.collectData.linkModal.termsAndConditions.link',
                'https://deeptrue.com/de/terms-and-conditions'
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(
                'project.collectData.linkModal.termsAndConditions.label',
                'Terms & Conditions'
              )}
            </a>
          ),
        }}
      />
    ),
    [t]
  );
  return (
    <div>
      <Modal
        isVisible={isVisible}
        onBackdropClick={onBackdropClick}
        onCloseClick={onCloseClick}
      >
        <Header>
          <Title>
            {t('project.collectData.linkModal.title', 'Start data collection')}
          </Title>
          <Typography fontWeight="regular" color="#5C6370">
            {t(
              'project.collectData.linkModal.subtitle',
              'Terms and conditions to start'
            )}
          </Typography>
        </Header>

        <Content>
          {t(
            'project.collectData.linkModal.content',
            `Deeptrue will collect the data from the completed surveys. You will find results in report section on your dashboard.\n\nThe cost of collecting the data is {credits} credits. The cost will be deducted from your balance of credits.`,
            {
              credits,
            }
          )}
        </Content>
        <TermsWrapper>
          <Checkbox
            // @ts-ignore
            checked={isAccepted}
            // @ts-ignore
            value={isAccepted}
            onChange={() => setIsAccepted(!isAccepted)}
            data-testid="terms-conditions"
          />
          <Terms>{modalTermsContent}</Terms>
        </TermsWrapper>
        {isVoucher && (
          <VoucherInfo
            discountPercent={voucher.voucher.discount_percent}
            projectPrice={projectPrice}
            projectPriceBeforeDiscount={projectPriceBeforeDiscount}
          />
        )}
        <ButtonWrapper>
          <SmallButton variant="outlined" onClick={onCloseClick}>
            {t('modal.cancel', 'Cancel')}
          </SmallButton>
          <SmallButton
            ml="0.5rem"
            disabled={!isAccepted}
            variant="primary"
            onClick={onAcceptTerms}
            data-testid="start-downloading"
            isLoading={isLoading}
          >
            {t(
              'project.collectData.automatic.submit',
              'Start for {credits} credits',
              { credits }
            )}
          </SmallButton>
        </ButtonWrapper>
      </Modal>
    </div>
  );
};

export default DataCollectionLinkModal;
